import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  getLatePunchoutThreshold,
  updateLatePunchoutThreshold,
  getTurboCooldownThreshold,
  updateTurboCooldownThreshold,
} from "../../payrollSlice";

import "./PayrollTab.scss";

// Valid = a whole number of minutes strictly greater than zero (mirrors the
// backend rule that these thresholds must be > 0).
const isValid = (raw: string): boolean =>
  /^\d+$/.test(raw.trim()) && Number(raw.trim()) > 0;

// ThresholdField renders one editable whole-minutes setting: label, input,
// hint, inline validation, and its own save button. Each setting saves
// independently against server truth (seeded from `serverValue`).
interface ThresholdFieldProps {
  id: string;
  label: string;
  hint: React.ReactNode;
  serverValue?: number;
  saving: boolean;
  onSave: (minutes: number) => void;
}

function ThresholdField({
  id,
  label,
  hint,
  serverValue,
  saving,
  onSave,
}: ThresholdFieldProps) {
  const [value, setValue] = React.useState<string | undefined>(undefined);

  // Seed / reset the local form whenever server truth changes
  // (initial load and the post-save refetch).
  React.useEffect(() => {
    if (serverValue !== undefined) setValue(String(serverValue));
  }, [serverValue]);

  const invalid = value !== undefined && !isValid(value);
  const isDirty =
    value !== undefined &&
    serverValue !== undefined &&
    value.trim() !== String(serverValue);
  const canSave = value !== undefined && isValid(value) && isDirty && !saving;

  return (
    <div className={`field-container${invalid ? " has-error" : ""}`}>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="minutes-input">
        <input
          id={id}
          type="number"
          min={1}
          step={1}
          value={value ?? ""}
          aria-label={label}
          aria-invalid={invalid}
          onChange={(e) => setValue(e.target.value)}
        />
        <span className="suffix">minutes</span>
      </div>
      <span className="field-hint">{hint}</span>
      {invalid && (
        <span className="form-error">
          Enter a whole number of minutes (1 or more).
        </span>
      )}
      <div className="payroll-footer">
        <button
          type="button"
          className="submit-button"
          onClick={() => value !== undefined && canSave && onSave(Number(value))}
          disabled={!canSave}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

export function PayrollTab() {
  const dispatch = useAppDispatch();

  const lateThreshold = useAppSelector(
    (s) => s.payroll.latePunchoutThresholdMinutes
  );
  const lateLoadAPI = useAppSelector((s) => s.payroll.getLatePunchoutThreshold);
  const lateSaveAPI = useAppSelector(
    (s) => s.payroll.updateLatePunchoutThreshold
  );

  const cooldownThreshold = useAppSelector(
    (s) => s.payroll.turboCooldownThresholdMinutes
  );
  const cooldownLoadAPI = useAppSelector(
    (s) => s.payroll.getTurboCooldownThreshold
  );
  const cooldownSaveAPI = useAppSelector(
    (s) => s.payroll.updateTurboCooldownThreshold
  );

  // Load both settings once on mount.
  React.useEffect(() => {
    dispatch(getLatePunchoutThreshold());
    dispatch(getTurboCooldownThreshold());
  }, [dispatch]);

  // Surface load failures.
  React.useEffect(() => {
    const failed =
      (!lateLoadAPI.loading && lateLoadAPI.error) ||
      (!cooldownLoadAPI.loading && cooldownLoadAPI.error);
    if (failed) {
      toast.error("Failed to load payroll settings. Please try again.");
    }
  }, [
    lateLoadAPI.loading,
    lateLoadAPI.error,
    cooldownLoadAPI.loading,
    cooldownLoadAPI.error,
  ]);

  const stillLoading =
    (lateLoadAPI.loading && lateThreshold === undefined) ||
    (cooldownLoadAPI.loading && cooldownThreshold === undefined);

  const saveLate = async (minutes: number) => {
    const result = await dispatch(updateLatePunchoutThreshold(minutes));
    if (updateLatePunchoutThreshold.fulfilled.match(result)) {
      toast.success("Payroll settings saved.");
    } else {
      toast.error("Failed to save payroll settings. Please try again.");
    }
  };

  const saveCooldown = async (minutes: number) => {
    const result = await dispatch(updateTurboCooldownThreshold(minutes));
    if (updateTurboCooldownThreshold.fulfilled.match(result)) {
      toast.success("Payroll settings saved.");
    } else {
      toast.error("Failed to save payroll settings. Please try again.");
    }
  };

  return (
    <div className="tab-content payroll-tab">
      <div className="payroll-header">
        <h2>Payroll</h2>
        <p className="payroll-subtitle">
          Settings that affect how driver hours are reviewed for payroll.
        </p>
      </div>

      {stillLoading ? (
        <div className="section-loading">
          <Oval color="#0A60BB" height={28} width={28} secondaryColor="#DAE0E6" />
        </div>
      ) : (
        <>
          <ThresholdField
            id="late-punchout-threshold"
            label="Late punch-out threshold"
            hint={
              <>
                Minutes a punch-out may trail the truck&rsquo;s last engine
                shut-off before it&rsquo;s flagged as late.
              </>
            }
            serverValue={lateThreshold}
            saving={lateSaveAPI.loading}
            onSave={saveLate}
          />
          <ThresholdField
            id="engine-cooldown-threshold"
            label="Engine cool-down threshold"
            hint={
              <>
                Minutes a diesel should idle to cool down after arriving before
                the engine is shut off; a shorter cool-down is flagged.
              </>
            }
            serverValue={cooldownThreshold}
            saving={cooldownSaveAPI.loading}
            onSave={saveCooldown}
          />
        </>
      )}
    </div>
  );
}
